import { regionFromCenterCode } from "pages/procurement/constants"

const emptyMoney = () => ({
  total: 0,
  committed: 0,
  approvedUnpaid: 0,
  approvedPaid: 0,
  remaining: 0,
})

const addMoney = (acc, row) => {
  acc.total += Number(row.total) || 0
  acc.committed += Number(row.committed) || 0
  acc.approvedUnpaid += Number(row.approvedUnpaid) || 0
  acc.approvedPaid += Number(row.approvedPaid) || 0
  acc.remaining += Number(row.remaining) || 0
  return acc
}

export const usedPct = (row) => {
  const total = Number(row.total) || 0
  if (total <= 0) return 0
  const consumed =
    (Number(row.committed) || 0) +
    (Number(row.approvedUnpaid) || 0) +
    (Number(row.approvedPaid) || 0)
  return (consumed / total) * 100
}

export const totalConsumed = (row) =>
  (Number(row.committed) || 0) +
  (Number(row.approvedUnpaid) || 0) +
  (Number(row.approvedPaid) || 0)

const leaf = (row, id, name, extra = {}) => ({
  id,
  name,
  level: extra.level || "leaf",
  ...emptyMoney(),
  ...extra,
  children: [],
  source: row,
})

// Rows from GET /budgets items[] carry category + center, not department or
// item group. Group only by dimensions that actually exist.
export const buildBudgetTree = (rows = [], groupBy = "center") => {
  if (!rows.length) return []

  if (groupBy === "category") {
    const map = new Map()
    rows.forEach((r) => {
      const id = `cat-${r.category ?? r.categoryId ?? r.categoryName}`
      if (!map.has(id)) {
        map.set(
          id,
          leaf(r, id, r.categoryName || "—", { level: "category", categoryId: r.category })
        )
      }
      const node = map.get(id)
      addMoney(node, r)
      const cid = r.centerId
      if (cid != null) {
        let child = node.children.find((c) => c.centerId === cid)
        if (!child) {
          child = leaf(r, `${id}-c-${cid}`, r.centerName || String(cid), {
            level: "center",
            centerId: cid,
            region: regionFromCenterCode(r.centerCode),
          })
          node.children.push(child)
        }
        addMoney(child, r)
      }
    })
    return Array.from(map.values())
  }

  // default: center → category  (region is derived, not a separate API level)
  const map = new Map()
  rows.forEach((r) => {
    const cid = r.centerId ?? "none"
    const id = `ctr-${cid}`
    if (!map.has(id)) {
      map.set(
        id,
        leaf(r, id, r.centerName || "Unassigned center", {
          level: "center",
          centerId: r.centerId,
          region: regionFromCenterCode(r.centerCode),
        })
      )
    }
    const node = map.get(id)
    addMoney(node, r)
    const catId = r.category ?? r.categoryId
    if (catId != null || r.categoryName) {
      const kid = `${id}-cat-${catId ?? r.categoryName}`
      let child = node.children.find((c) => c.id === kid)
      if (!child) {
        child = leaf(r, kid, r.categoryName || "—", { level: "category", categoryId: catId })
        node.children.push(child)
      }
      addMoney(child, r)
    }
  })
  return Array.from(map.values())
}

export const sortTree = (nodes, getVal, dir = "desc") => {
  const mul = dir === "asc" ? 1 : -1
  return [...(nodes || [])]
    .sort((a, b) => {
      const va = getVal(a)
      const vb = getVal(b)
      if (va == null) return 1
      if (vb == null) return -1
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * mul
      return String(va).localeCompare(String(vb)) * mul
    })
    .map((n) => ({ ...n, children: sortTree(n.children || [], getVal, dir) }))
}

export const flattenTree = (nodes, expanded) => {
  const out = []
  const walk = (list, depth) => {
    list.forEach((n) => {
      const hasKids = n.children && n.children.length > 0
      out.push({ ...n, depth, expandable: hasKids, isParent: hasKids && depth === 0 })
      if (hasKids && expanded.has(n.id)) walk(n.children, depth + 1)
    })
  }
  walk(nodes, 0)
  return out
}
